// `apps/web` entry point.
//
// Boots Sentry + PostHog (both no-ops without their respective keys), mints a
// stable correlation id for the page-load, builds the singleton ApiClient,
// fires Web Vitals into PostHog, and hands everything to `<App>`.
//
// Web Wave 9 — env names mirror `apps/web/.env.local`:
//   VITE_SENTRY_DSN_WEB             — Sentry browser DSN (DE region).
//   VITE_SENTRY_ENV / MODE          — environment label.
//   VITE_SENTRY_RELEASE             — commit SHA / semver.
//   VITE_POSTHOG_PROJECT_API_KEY    — PostHog project API key (EU host).
//   VITE_POSTHOG_HOST               — defaults to https://eu.i.posthog.com.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { createApiClient } from '@barel/api-client';
import { mintCorrelationId } from '@barel/telemetry';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from '@barel/i18n';
// Token CSS — lands the design-system CSS variables at the document level
// before React mounts so the first paint already has resolved theme tokens.
import '@barel/ui/tokens.css';
// Tailwind base/components/utilities — must come after tokens so Preflight
// resets the document but utility classes can still resolve `var(--token)`s.
import '@/index.css';

import { App } from '@/App';
import { bootAnalytics } from '@/lib/analytics';
import { bootSentry } from '@/lib/sentry';
import { reportWebVitals } from '@/lib/webVitals';

interface ViteEnv {
  readonly VITE_API_BASE_URL?: string;
  readonly VITE_SENTRY_DSN?: string;
  readonly VITE_SENTRY_DSN_WEB?: string;
  readonly VITE_SENTRY_ENV?: string;
  readonly VITE_SENTRY_RELEASE?: string;
  readonly VITE_POSTHOG_KEY?: string;
  readonly VITE_POSTHOG_PROJECT_API_KEY?: string;
  readonly VITE_POSTHOG_HOST?: string;
}

const env = import.meta.env as ImportMetaEnv & ViteEnv;

// Sentry — no-op when DSN is missing (hard rule from the build prompt). We
// honour both `VITE_SENTRY_DSN_WEB` (canonical, Wave 9) and the legacy
// `VITE_SENTRY_DSN` so older `.env.local` files keep working.
bootSentry({
  dsn: env.VITE_SENTRY_DSN_WEB ?? env.VITE_SENTRY_DSN,
  env: env.VITE_SENTRY_ENV ?? env.MODE,
  release: env.VITE_SENTRY_RELEASE,
});

// PostHog — autocapture ON, capture_pageview ON, session recording OFF (per
// Wave 9 privacy posture). Mandatory telemetry per P12 — no user toggle.
bootAnalytics({
  posthogKey: env.VITE_POSTHOG_PROJECT_API_KEY ?? env.VITE_POSTHOG_KEY,
  posthogHost: env.VITE_POSTHOG_HOST,
});

// Web Vitals → PostHog. Fire-and-forget; the underlying analytics client
// no-ops when PostHog isn't configured.
reportWebVitals();

// Stable per-page-load correlation id stamped on every fetch from the
// singleton client. Per-request unique ids still get minted by the wrapped
// fetch in `@barel/api-client`; this one is the boot-time anchor.
const correlationId = mintCorrelationId();

const apiBaseUrl = env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

const apiClient = createApiClient({
  baseUrl: apiBaseUrl,
  headers: () => ({
    'X-Correlation-Page-Id': correlationId,
  }),
});

const initialLocale = ((): Locale => {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const candidate = navigator.language?.slice(0, 2).toLowerCase();
  if (candidate && (SUPPORTED_LOCALES as readonly string[]).includes(candidate)) {
    return candidate as Locale;
  }
  return DEFAULT_LOCALE;
})();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root missing from index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <App apiClient={apiClient} apiBaseUrl={apiBaseUrl} initialLocale={initialLocale} />
  </StrictMode>,
);
